import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, catchError, map, shareReplay } from 'rxjs';
import { environment } from '../environments/environment';

/**
 * Interface representing tenant branding configuration
 */
export interface TenantBranding {
  tenant_name: string;
  display_name: string;
  logo_url?: string;
  logo_base64?: string;
  tag_line?: string;
  primary_color?: string;
}

/**
 * Default branding fallback
 */
const DEFAULT_BRANDING: TenantBranding = {
  tenant_name: '',
  display_name: '',
  tag_line: 'Welcome back! Please sign in to continue.',
};

/**
 * Service to fetch and cache tenant branding information
 * This service calls the public branding API which doesn't require authentication
 */
@Injectable({
  providedIn: 'root'
})
export class BrandingService {
  private readonly brandingUrl = `${environment.authUrl}/public/branding`;
  private brandingCache$: Observable<TenantBranding> | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Gets the tenant branding from the API
   * Results are cached to avoid multiple API calls
   */
  getBranding(): Observable<TenantBranding> {
    // Return cached observable if available
    if (this.brandingCache$) {
      return this.brandingCache$;
    }

    // Fetch branding and cache the result
    this.brandingCache$ = this.http.get<TenantBranding>(this.brandingUrl).pipe(
      map(branding => ({
        ...DEFAULT_BRANDING,
        ...branding,
      })),
      catchError(error => {
        console.error('Error fetching tenant branding:', error);
        // Return default branding on error
        return of({
          ...DEFAULT_BRANDING,
          display_name: this.getDefaultDisplayName(),
        });
      }),
      // Share the result across all subscribers
      shareReplay(1)
    );

    return this.brandingCache$;
  }

  /**
   * Clears the branding cache (useful for testing or when tenant changes)
   */
  clearCache(): void {
    this.brandingCache$ = null;
  }

  /**
   * Gets a default display name from the hostname
   */
  private getDefaultDisplayName(): string {
    try {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length > 0 && parts[0]) {
        const tenant = parts[0];
        return tenant.charAt(0).toUpperCase() + tenant.slice(1);
      }
    } catch (e) {
      console.error('Error getting default display name:', e);
    }
    return 'NuvertOS';
  }
}
